import React, { useState, useEffect } from 'react';
import { Project } from '../../types/project';
import { ResearchGenerator } from './ResearchGenerator';
import { ResearchLoadingState } from './ResearchLoadingState';
import { MarkdownViewer } from './MarkdownViewer';
import { updateProject } from '../../services/api';

interface ResearchTabProps {
  projectId: string;
  project: Project;
}

type ResearchState = 'idle' | 'generating' | 'complete';

/**
 * Main research tab component
 * Manages research generation workflow and displays results
 */
export const ResearchTab: React.FC<ResearchTabProps> = ({ projectId, project }) => {
  const [researchState, setResearchState] = useState<ResearchState>('idle');
  const [isEditing, setIsEditing] = useState(false);
  const [editedContent, setEditedContent] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  // Determine current state based on project data
  useEffect(() => {
    if (project.researchReportMd) {
      setResearchState('complete');
    } else if (researchState === 'generating') {
      // Keep generating state until report is ready
      // Real-time listener will update project when complete
    } else {
      setResearchState('idle');
    }
  }, [project.researchReportMd, researchState]);

  const handleGenerationStart = () => {
    setResearchState('generating');
  };

  const handleEdit = () => {
    setEditedContent(project.researchReportMd || '');
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditedContent('');
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await updateProject(projectId, {
        researchReportMd: editedContent,
      });
      setIsEditing(false);
      setIsSaving(false);
    } catch (error) {
      console.error('Failed to save research:', error);
      setIsSaving(false);
      alert('Failed to save changes. Please try again.');
    }
  };

  // Render based on state
  if (researchState === 'complete' && project.researchReportMd) {
    return (
      <div className="space-y-6">
        {/* Page Header */}
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Market Research</h2>
          <p className="text-gray-600">AI-generated deep research insights for your project</p>
        </div>

        {/* Status Header with Edit/Regenerate Options */}
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <svg className="w-6 h-6 text-accent-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  {isEditing ? 'Editing Research Report' : 'Research Report Complete'}
                </h3>
                <p className="text-sm text-gray-600">
                  {isEditing ? 'Make your changes and click Save' : `Generated on ${new Date(project.updatedAt).toLocaleDateString()}`}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {isEditing ? (
                <>
                  <button
                    onClick={handleCancelEdit}
                    disabled={isSaving}
                    className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={isSaving}
                    className="px-4 py-2 text-sm font-medium text-white bg-primary-500 hover:bg-primary-700 rounded-md transition-colors disabled:opacity-50"
                  >
                    {isSaving ? 'Saving...' : 'Save Changes'}
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={handleEdit}
                    className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors"
                  >
                    <svg className="w-4 h-4 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                    Edit
                  </button>
                  <button
                    onClick={() => setResearchState('idle')}
                    className="px-4 py-2 text-sm font-medium text-primary-500 hover:text-primary-700 hover:bg-primary-50 rounded-md transition-colors"
                  >
                    Regenerate
                  </button>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Markdown Report or Editor */}
        {isEditing ? (
          <div className="bg-white rounded-lg shadow p-6">
            <textarea
              value={editedContent}
              onChange={(e) => setEditedContent(e.target.value)}
              className="w-full h-[600px] px-4 py-3 border border-gray-300 rounded-md font-mono text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 resize-y"
              placeholder="Edit your research report markdown..."
            />
            <p className="mt-2 text-sm text-gray-500">
              Tip: Use Markdown formatting for headers, lists, and emphasis.
            </p>
          </div>
        ) : (
          <MarkdownViewer content={project.researchReportMd} />
        )}
      </div>
    );
  }

  if (researchState === 'generating') {
    return <ResearchLoadingState />;
  }

  // Idle state - show generator
  return (
    <div>
      {/* Page Header */}
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Market Research</h2>
        <p className="text-gray-600">Generate AI-powered deep research insights for your project</p>
      </div>

      <ResearchGenerator
        projectId={projectId}
        projectName={project.name}
        onGenerationStart={handleGenerationStart}
      />
    </div>
  );
};
